import React, { FC, useState } from 'react';

import {
  Box,
  Flex,
  Modal,
  Stack,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import * as yup from 'yup';

import { Form, Formik } from 'formik';

import { PackageSelect } from '../PackageSelect';

import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
import PlacesAutocomplete from 'components/primitives/PlacesAutocomplete';

import parsePlacesResult, { unitToKeep } from 'utils/parsePlacesResult';

import { CreateWashHubModalViewProps } from './CreateWashHubModal.props';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const validationSchema = yup.object().shape({
  name: yup.string().required('Wash hub name is required'),
  email: yup.string().email('Enter a valid email address'),
  type: yup.string().required(),
  hubSize: yup.string().required('Hub size is required'),
  phcMethod: yup.number().optional(),
  address: yup
    .object()
    .typeError('Address is a required field')
    .shape({
      addressLine1: yup.string(),
      addressLine2: yup.string(),
      city: yup.string(),
      state: yup.string(),
      zip: yup
        .string()
        .required('Zip code is a required field')
        .matches(/^[0-9]*$/, 'Invalid format (zip code must be a number).'),
    })
    .required('Address is a required field'),
  packages: yup.array().of(yup.string()),
});

const View: FC<CreateWashHubModalViewProps> = (props) => {
  const { isOpen, onClose, onCreate, isLoading } = props;
  const [showZipInput, setShowZipInput] = useState(false);

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader textAlign="center" fontWeight="600">
          Create Wash Hub
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" p={6}>
            <Formik
              initialValues={{
                name: '',
                email: '',
                hubSize: '',
                phcMethod: '',
                type: 'wash_hub',
                address: {
                  addressLine1: '',
                  addressLine2: '',
                  city: '',
                  state: '',
                  zip: '',
                },
                packages: [],
              }}
              validationSchema={validationSchema}
              onSubmit={onCreate}>
              {({ setFieldValue, isValid, values }) => (
                <Form>
                  <Flex>
                    <FormikInput
                      name="name"
                      label="Name (*)"
                      labelProps={labelProps}
                      placeholder="Your text here"
                    />
                    <Box h="20px" w="50px" />{' '}
                    <FormikInput
                      type="number"
                      name="hubSize"
                      label="Hub Size (Sq Ft)"
                      labelProps={labelProps}
                      placeholder="Your text here"
                    />
                  </Flex>
                  <Box h="20px" />
                  <FormikSelectInput
                    name="phcMethod"
                    label="Pickup Harvest Counting Method"
                    labelProps={labelProps}
                    placeholder="Select method">
                    <option value={0}>Rack</option>
                    <option value={1}>Stack</option>
                  </FormikSelectInput>
                  <Box h="20px" />
                  <PlacesAutocomplete
                    name="address"
                    labelText="Address (*)"
                    labelProps={labelProps}
                    onSelect={(result) => {
                      const parsed = parsePlacesResult(result);

                      // addressLine1 is the street; addressLine2 the unit
                      // Google gave, else the one typed below, never a copied
                      // address.
                      setFieldValue('address', {
                        ...parsed,
                        addressLine2: unitToKeep(
                          parsed.addressLine2,
                          values?.address?.addressLine2,
                        ),
                      });

                      const shouldToCheckForShowZipInput =
                        result.length > 0 && !Boolean(parsed.zip);

                      if (shouldToCheckForShowZipInput) {
                        setShowZipInput(true);
                      }
                    }}
                  />

                  {showZipInput && (
                    <>
                      <Box h="20px" w="20px" />
                      <FormikInput
                        name="address.zip"
                        label="Zip code (*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1 }}
                        placeholder="your text here"
                        onInput={(value) => {
                          setFieldValue('address.zip', value);
                        }}
                      />
                    </>
                  )}

                  <Box h="20px" />
                  <FormikInput
                    name="address.addressLine2"
                    label="Address 2"
                    labelProps={labelProps}
                    containerProps={{ flex: 1 }}
                    placeholder="Unit 300"
                    type="text"
                  />

                  <Box h="20px" />
                  <FormikInput
                    name="email"
                    label="Email"
                    labelProps={labelProps}
                    containerProps={{ flex: 1 }}
                    placeholder="hub@example.com"
                    type="email"
                  />

                  <PackageSelect values={values.packages} />
                  <Stack my={8} align="center">
                    <Button
                      w="200px"
                      type="submit"
                      isLoading={isLoading}
                      isDisabled={!isValid}>
                      Create
                    </Button>
                    <Button
                      variant="outline"
                      w="200px"
                      onClick={onClose}
                      isDisabled={isLoading}>
                      Cancel
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default View;
